/**
 * Сарын нүд — Даваагаас эхэлнэ (Монголын хуанли).
 *
 * Нүдний дэвсгэрийн өнгө = тэр өдрийн орлого сарын хамгийн их өдөртэй
 * харьцуулахад (гурван шат). Өргөн дэлгэцэд дүн, ширхэг, агуулахад
 * орсон тоо бичигдэнэ; гар утсанд (7 багана ≈ 40px) зөвхөн өнгө, цэг —
 * тоо нь өдрийн дэлгэрэнгүйд.
 *
 * ⚠️ Хоосон өдөр ч дарагдана: шилжүүлэг, актлалт, баталгаа нүдэнд
 *    харагддаггүй ч дэлгэрэнгүйд байж болно. Ирээдүйн өдөр л хаалттай.
 */

import {
  formatDate, formatMnt, formatMntShort, formatQty, weekdayOfDateKey, type CalendarDay,
} from '@boots/domain';

const WEEK = ['Да', 'Мя', 'Лх', 'Пү', 'Ба', 'Бя', 'Ня'];

/** Орлогын шат. Мөнгөөр тооцоо хийхгүй — зөвхөн өнгө сонгох харьцаа. */
function heat(salesMnt: string, max: number): string {
  const ratio = max > 0 ? Number(salesMnt) / max : 0;
  if (ratio <= 0) return 'bg-panel';
  if (ratio < 1 / 3) return 'bg-accent-100/50';
  if (ratio < 2 / 3) return 'bg-accent-100';
  return 'bg-accent-200';
}

function describe(d: CalendarDay): string {
  const parts = [`${formatDate(d.date)}, ${weekdayOfDateKey(d.date).label}`];
  if (d.orders > 0) parts.push(`борлуулалт ${formatMnt(d.salesMnt)}`, `${formatQty(d.units)} ш зарагдсан`);
  if (d.warehouseIn > 0) parts.push(`${formatQty(d.warehouseIn)} ш агуулахад орсон`);
  if (d.returns > 0) parts.push(`${formatQty(d.returns)} буцаалт`);
  return parts.join(' · ');
}

export function MonthGrid({ days, today, selected, onPick }: {
  days: CalendarDay[];
  today: string;
  selected: string | null;
  onPick: (date: string) => void;
}) {
  const lead = Number(weekdayOfDateKey(days[0]!.date).key) - 1;
  const max = Math.max(0, ...days.map((d) => Number(d.salesMnt)));

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {WEEK.map((w, i) => (
          <span key={w} className={`text-center text-label ${i >= 5 ? 'text-ink-3' : 'text-ink-2'}`}>
            {w}
          </span>
        ))}
        {Array.from({ length: lead }, (_, i) => <span key={`lead-${i}`} aria-hidden />)}
        {days.map((d) => (
          <DayCell
            key={d.date}
            day={d}
            max={max}
            isToday={d.date === today}
            isFuture={d.date > today}
            isSelected={d.date === selected}
            onPick={onPick}
          />
        ))}
      </div>
      <Legend />
    </div>
  );
}

function DayCell({ day: d, max, isToday, isFuture, isSelected, onPick }: {
  day: CalendarDay;
  max: number;
  isToday: boolean;
  isFuture: boolean;
  isSelected: boolean;
  onPick: (date: string) => void;
}) {
  return (
    <button
      type="button"
      disabled={isFuture}
      onClick={() => onPick(d.date)}
      aria-label={describe(d)}
      aria-pressed={isSelected}
      className={`flex min-h-14 min-w-0 flex-col items-start gap-0.5 overflow-hidden rounded-well border p-1.5
        text-left transition hover:border-accent-300 disabled:cursor-default disabled:opacity-40
        disabled:hover:border-hair sm:min-h-24 sm:p-2 ${heat(d.salesMnt, max)} ${
        isSelected ? 'border-accent ring-2 ring-accent/40' : 'border-hair'
      }`}
    >
      <span className="flex w-full items-center justify-between gap-1">
        <span
          className={`num text-meta ${
            isToday ? 'grid size-6 place-items-center rounded-full bg-accent-fill font-semibold text-white' : 'text-ink-2'
          }`}
        >
          {Number(d.date.slice(8))}
        </span>
        {d.returns > 0 ? (
          <span className="num hidden text-micro text-alert-ink sm:inline">↩ {formatQty(d.returns)}</span>
        ) : null}
      </span>

      {/* ── Таблет ба дэсктоп: тоо ─────────────────────────── */}
      {d.orders > 0 ? (
        <>
          <span className="num hidden max-w-full truncate text-meta font-semibold sm:block">
            {formatMntShort(d.salesMnt)}
          </span>
          <span className="num hidden text-micro text-ink-2 sm:block">{formatQty(d.units)} ш зарсан</span>
        </>
      ) : null}
      {d.warehouseIn > 0 ? (
        <span className="num mt-auto hidden rounded-pill bg-info-bg px-1.5 text-micro text-info-ink sm:inline">
          +{formatQty(d.warehouseIn)} ш
        </span>
      ) : null}

      {/* ── Гар утас: цэг ──────────────────────────────────── */}
      <span aria-hidden className="mt-auto flex gap-1 sm:hidden">
        {d.orders > 0 ? <span className="size-1.5 rounded-full bg-accent" /> : null}
        {d.warehouseIn > 0 ? <span className="size-1.5 rounded-full bg-info-ink" /> : null}
        {d.returns > 0 ? <span className="size-1.5 rounded-full bg-alert" /> : null}
      </span>
    </button>
  );
}

function Legend() {
  const swatch = 'inline-block size-3 rounded-[3px] border border-hair';
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-label text-ink-3">
      <span className="flex items-center gap-1.5">
        <span className={`${swatch} bg-accent-100/50`} />
        <span className={`${swatch} bg-accent-100`} />
        <span className={`${swatch} bg-accent-200`} />
        өдрийн борлуулалт
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-1.5 rounded-full bg-accent sm:hidden" />
        <span className="hidden sm:inline">«3 ш зарсан»</span>
        зарагдсан гутал
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-1.5 rounded-full bg-info-ink sm:hidden" />
        <span className="hidden rounded-pill bg-info-bg px-1.5 text-info-ink sm:inline">+12 ш</span>
        агуулахад орж ирсэн
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-1.5 rounded-full bg-alert sm:hidden" />
        <span className="hidden text-alert-ink sm:inline">↩ 1</span>
        буцаалт
      </span>
    </div>
  );
}
